import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, ArrowUp } from 'lucide-react';
import { websiteData } from '../data/websiteData.js';

// Custom inline SVG components for brand social icons
const FacebookIcon = () => (
  <svg 
    viewBox="0 0 24 24" 
    width="16" 
    height="16" 
    stroke="currentColor" 
    strokeWidth="2" 
    fill="none" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
  </svg>
);

const InstagramIcon = () => (
  <svg 
    viewBox="0 0 24 24" 
    width="16" 
    height="16" 
    stroke="currentColor" 
    strokeWidth="2" 
    fill="none" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
);

const LinkedinIcon = () => (
  <svg 
    viewBox="0 0 24 24" 
    width="16" 
    height="16" 
    stroke="currentColor" 
    strokeWidth="2" 
    fill="none" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
    <rect x="2" y="9" width="4" height="12" />
    <circle cx="4" cy="4" r="2" />
  </svg>
);

export default function Footer() {
  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'About Us', path: '/about' },
    { name: 'Contact Us', path: '/contact' }
  ];

  const socialLinks = [
    { icon: FacebookIcon, link: "#", label: "Facebook" },
    { icon: InstagramIcon, link: "#", label: "Instagram" },
    { icon: LinkedinIcon, link: "#", label: "LinkedIn" }
  ];

  const currentYear = new Date().getFullYear();

  const handleBackToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <footer className="footer" aria-label="Vijay Industry Footer">
      <div className="container">
        {/* Footer Top Grid */}
        <div className="footer-top">
          {/* Column 1: Brand Logo & Tagline */}
          <div className="footer-brand">
            <div className="footer-logo-container">
              <img 
                src="/logo2.png" 
                alt="Vijay Industry" 
                style={{ height: '50px', objectFit: 'contain', display: 'block' }} 
              />
            </div>
            <p className="footer-brand-tagline" style={{ marginTop: '1rem' }}>
              {websiteData.tagline}
            </p>
            <p className="footer-brand-sub">
              {websiteData.taglineSecondary} for residential, commercial, and industrial engineering constructions.
            </p>
          </div>

          {/* Column 2: Quick Links */}
          <div className="footer-col">
            <h3>Quick Links</h3>
            <ul className="footer-links">
              {quickLinks.map((link) => (
                <li key={link.path} className="footer-link-item">
                  <Link to={link.path}>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Services */}
          <div className="footer-col">
            <h3>Services</h3>
            <ul className="footer-links">
              {websiteData.services.map((service) => (
                <li key={service.id} className="footer-link-item">
                  <Link to="/services">
                    {service.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Contact Information */}
          <div className="footer-col">
            <h3>Contact Info</h3>
            <div className="footer-contact-info">
              <div className="footer-contact-item">
                <Phone size={16} className="footer-contact-icon" />
                <a href={`tel:${websiteData.contact.phone}`} style={{ color: 'inherit' }}>
                  {websiteData.contact.phoneDisplay}
                </a>
              </div>
              <div className="footer-contact-item">
                <Mail size={16} className="footer-contact-icon" />
                <a href={`mailto:${websiteData.contact.email}`} style={{ color: 'inherit', wordBreak: 'break-all' }}>
                  {websiteData.contact.email}
                </a>
              </div>
              <div className="footer-contact-item">
                <MapPin size={16} className="footer-contact-icon" />
                <span>{websiteData.contact.address}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Bottom Bar */}
        <div className="footer-bottom">
          <div className="footer-copy">
            &copy; {currentYear} Vijay Industry. All Rights Reserved.
          </div>

          {/* Social Icons & Back to Top */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
            <div className="footer-socials">
              {socialLinks.map((social, index) => {
                const IconComponent = social.icon;
                return (
                  <a 
                    key={index} 
                    href={social.link} 
                    className="footer-social-link" 
                    aria-label={`Visit our ${social.label} page`}
                  >
                    <IconComponent />
                  </a>
                );
              })}
            </div>
            
            <button 
              onClick={handleBackToTop}
              className="footer-social-link"
              style={{ border: 'none', cursor: 'pointer' }}
              aria-label="Scroll to top of the page"
            >
              <ArrowUp size={16} />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
